import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Eye } from "lucide-react";
import { EyeClosedIcon } from "@radix-ui/react-icons";

import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { loginAdmin } from "@/config/mutation/admin";
import { useAuthContext } from "@/lib/use-auth";
import { createAdminActivity } from "@/config/mutation/super-admin";

const formSchema = z.object({
  email: z
    .string({
      required_error: "Email is required!",
    })
    .email({
      message: "Invalid Email",
    }),
  password: z.string({
    required_error: "Password required!",
  }),
});

type FormValues = z.infer<typeof formSchema>;

export const AdminLoginForm = () => {
  const navigate = useNavigate();
  const { setUser, setisAuth } = useAuthContext();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const [passwordView, setPasswordView] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: loginAdmin,
  });

  const { mutateAsync } = useMutation({
    mutationFn: createAdminActivity(),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    // console.log(values);

    mutate(values, {
      onSuccess: async (data) => {
        // console.log("onSucess Login", data);
        // const responseData = data as {
        //   key: string;
        // };

        await mutateAsync({
          admin: data?.admin?._id as string,
          mode: "LOGIN",
        });

        // setTimeout(() => {
        //   Cookies.set("key", data?.key, {
        //     expires: new Date(Date.now() + 24 * 60 * 60 * 1000),
        //   });
        // }, 2000);
        setUser(data?.key, "admin");
        setisAuth(true);
        toast.success("Login Successful!");
        navigate("/admin");
      },

      onError: (error) => {
        console.log("ERROR WHILE LOGGIN IN AS ADMIN", error);
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });
  }

  return (
    <div className="max-w-md w-full rounded-md">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    {...field}
                    className="border-white/50"
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={passwordView ? "text" : "password"}
                      {...field}
                      className="border-white/50"
                      disabled={isPending}
                    />
                    <Button
                      type="button"
                      size="icon"
                      className="w-5 h-5 absolute right-2 top-2 z-[2] bg-[#f4ca8d] text-[#01020d] hover:bg-[#f4ca8d]/80"
                      onClick={() => {
                        setPasswordView(!passwordView);
                      }}
                    >
                      {passwordView ? (
                        <Eye className="w-3 h-3" />
                      ) : (
                        <EyeClosedIcon className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="bg-[#f4ca8d] text-[#01020d] hover:bg-[#f4ca8d]/80"
            disabled={isPending}
          >
            Log in
          </Button>
        </form>
      </Form>
    </div>
  );
};
